import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import { Box, Button, Link, Stack, Typography } from "@mui/material";
import { useState } from "react";
import { ItemProperty, UserAutocomplete } from "src/components";
import { Links } from "src/constants";
import { useEditIssueCollaborators, UserDto, type IssueDto } from "src/http";

interface IProps {
  issue: IssueDto;
  onChanged?: (issueGuid: string, issueDescription: string, removedCollaborators: UserDto[]) => void;
}

const IssueCollaboratorsProperty = (props: IProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState<UserDto[]>(props.issue.collaborators ?? []);
  const { mutate: editCollaborators } = useEditIssueCollaborators();

  function handleCancelClicked() {
    setIsEditing(false);
    setSelectedUsers(props.issue.collaborators ?? []);
  }

  function handleSaveClicked() {
    setIsEditing(false);

    const removedCollaborators = props.issue.collaborators?.filter(
      (originalCollaborator) =>
        !selectedUsers.some((updatedOwner) => updatedOwner.userId === originalCollaborator.userId),
    );

    editCollaborators(
      {
        issueGuid: props.issue.guid,
        teamSlug: props.issue.assignedTeam?.slug,
        collaborators: selectedUsers,
      },
      {
        onSuccess: () => {
          props.onChanged?.(props.issue.guid, props.issue.description, removedCollaborators);
        },
      },
    );
  }

  return (
    <ItemProperty
      labelIcon={<GroupOutlinedIcon />}
      label="Collaborators"
      labelProps={{ sx: { top: 1 } }}
      canEdit={props.issue.permissions.userCanEditCollaborators}
      isEditing={isEditing}
      onEditClicked={() => setIsEditing(true)}
    >
      <>
        {isEditing ? (
          <Stack spacing={0.5} sx={{ flex: 1 }}>
            <UserAutocomplete
              value={selectedUsers}
              onChange={(value) => setSelectedUsers(value)}
              placeholder="Add additional collaborators..."
              autofocus
              multiple
            />

            <Stack direction="row" spacing={0.5} sx={{ justifyContent: "flex-end" }}>
              <Button variant="text" onClick={handleCancelClicked}>
                Cancel
              </Button>
              <Button variant="tertiary" onClick={handleSaveClicked}>
                Save
              </Button>
            </Stack>
          </Stack>
        ) : selectedUsers.length > 0 ? (
          <Stack spacing={0.5}>
            <Typography variant="body1">
              {[...selectedUsers]
                .sort((a, b) => a.displayName.localeCompare(b.displayName))
                .map((owner, index) => (
                  <Box key={index} component="span">
                    {index > 0 ? ", " : ""}
                    <Link onClick={(e) => e.stopPropagation()} href={Links.User(owner.userId)}>
                      {owner.displayName}
                      {owner.enabled ? "" : " (deactivated)"}
                    </Link>
                  </Box>
                ))}
            </Typography>
          </Stack>
        ) : (
          <Typography variant="placeholder">Add additional collaborators...</Typography>
        )}
      </>
    </ItemProperty>
  );
};

export { IssueCollaboratorsProperty };
